import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { FiX, FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import './CartDrawer.css';

const CartDrawer = () => {
  const {
    cart, cartOpen, setCartOpen,
    updateItem, removeItem, totalPrice,
    setCheckoutDrawerOpen,           // ← CheckoutDrawer open karne ke liye
  } = useCart();

  if (!cartOpen) return null;

  const items = cart?.items || [];
  const subtotal = Number(totalPrice);
  const shipping = subtotal >= 500 ? 0 : (subtotal > 0 ? 90 : 0);
  const remaining = Math.max(0, 500 - subtotal);

  const handleCheckout = () => {
    setCartOpen(false);
    setCheckoutDrawerOpen(true);     // ← CheckoutDrawer open, /checkout navigate nahi
  };

  return (
    <>
      <div className="cart-overlay" onClick={() => setCartOpen(false)} />

      <div className="cart-drawer">

        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="cart-drawer__header">
          <div>
            <h2 className="cart-drawer__title">Your Cart</h2>
            <p className="cart-drawer__count">
              {items.length} item{items.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            className="cart-drawer__close"
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* ── Free shipping progress bar ───────────────────────────── */}
        {subtotal > 0 && subtotal < 500 && (
          <div className="cart-drawer__freeship-bar">
            <div
              className="cart-drawer__freeship-fill"
              style={{ width: `${Math.min((subtotal / 500) * 100, 100)}%` }}
            />
            <p className="cart-drawer__freeship-text">
              Add <strong>₹{remaining.toFixed(0)}</strong> more for <strong>FREE shipping</strong>!
            </p>
          </div>
        )}
        {subtotal >= 500 && (
          <div className="cart-drawer__freeship-achieved">🎉 You've unlocked Free Shipping!</div>
        )}

        {/* ── Body ─────────────────────────────────────────────────── */}
        <div className="cart-drawer__body">
          {items.length === 0 ? (
            <div className="cart-drawer__empty">
              <div className="cart-drawer__empty-icon">🛒</div>
              <h3>Your cart is empty</h3>
              <p>Add some products to get started</p>
              <Link
                to="/"
                className="btn btn-primary"
                onClick={() => setCartOpen(false)}
                style={{ marginTop: '20px' }}
              >
                <FiShoppingBag size={16} /> Browse Products
              </Link>
            </div>
          ) : (
            <div className="cart-drawer__items">
              {items.map((item) => (
                <div key={item.id} className="cart-item">
                  {item.product_image && (
                    <img
                      src={item.product_image}
                      alt={item.product_name}
                      className="cart-item__img"
                    />
                  )}

                  <div className="cart-item__info">
                    {/* Name — 2 lines max */}
                    <p className="cart-item__name">{item.product_name}</p>
                    {item.variant?.pack_label && (
                      <p className="cart-item__variant">{item.variant.pack_label}</p>
                    )}

                    <div className="cart-item__row">
                      {/* Qty stepper */}
                      <div className="cart-item__qty">
                        <button
                          className="cart-item__qty-btn"
                          onClick={() => {
                            if (item.quantity > 1) updateItem(item.id, item.quantity - 1);
                            else removeItem(item.id);
                          }}
                          aria-label="Decrease"
                        >
                          <FiMinus size={11} />
                        </button>
                        <span className="cart-item__qty-val">{item.quantity}</span>
                        <button
                          className="cart-item__qty-btn"
                          onClick={() => updateItem(item.id, item.quantity + 1)}
                          aria-label="Increase"
                        >
                          <FiPlus size={11} />
                        </button>
                      </div>

                      {/* Price */}
                      <p className="cart-item__price">
                        ₹{Number(item.subtotal).toFixed(0)}
                      </p>
                    </div>
                  </div>

                  {/* Remove — top-right, no overlap */}
                  <button
                    className="cart-item__remove"
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove item"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────── */}
        {items.length > 0 && (
          <div className="cart-drawer__footer">
            {/* Mini summary */}
            <div className="cart-drawer__summary">
              <div className="cart-drawer__summary-row">
                <span>Subtotal ({items.length} item{items.length !== 1 ? 's' : ''})</span>
                <span>₹{subtotal.toFixed(0)}</span>
              </div>
              <div className="cart-drawer__summary-row">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'cart-drawer__free' : ''}>
                  {shipping === 0 ? 'FREE' : `₹${shipping}`}
                </span>
              </div>
              <div className="cart-drawer__summary-row cart-drawer__summary-row--total">
                <span>Estimated Total</span>
                <span>₹{(subtotal + shipping).toFixed(0)}</span>
              </div>
            </div>

            <div className="cart-drawer__actions">
              {/* View Cart — goes to /cart page */}
              <Link
                to="/cart"
                className="btn btn-outline cart-drawer__view-btn"
                onClick={() => setCartOpen(false)}
              >
                <FiShoppingBag size={15} /> View Cart
              </Link>

              {/* Checkout — opens CheckoutDrawer, NO page navigation */}
              <button
                className="btn btn-primary cart-drawer__checkout-btn"
                onClick={handleCheckout}
              >
                Checkout <FiArrowRight size={15} />
              </button>
            </div>

            <p className="cart-drawer__secure">🔒 100% Secure Checkout</p>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;